/* OnboardingWizard — modal de bienvenue bloquant à la 1ère connexion.
 *
 * Règles :
 *   - Bloquant : ne se ferme pas tant que l'utilisateur n'a pas complété OU
 *     choisi explicitement « Continuer plus tard » (l'organisation reste alors
 *     `onboarding_completed=false` mais le modal ne réapparaît qu'à la prochaine session).
 *   - Une seule fois : dès `onboarding_completed=true` sur l'org active, le
 *     modal ne réapparaît plus.
 *   - Non affiché pour les comptes démo (`is_demo=true`) — la démo est déjà peuplée.
 *   - Design cohérent avec la charte Fonbex existante (dialog + boutons pill).
 *
 * 4 étapes :
 *   1. Bienvenue
 *   2. Profil patrimonial (Investisseur / SCI / Holding / Marchand de biens / Mixte)
 *   3. Coordonnées organisation (nom, SIREN, ville) — tout optionnel
 *   4. Démarrer : espace vide OU charger un jeu de démonstration dans MON compte
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Building2, Landmark, HandCoins, Layers, Users,
  ArrowRight, ArrowLeft, PartyPopper, CheckCircle2, Rocket,
} from "lucide-react";
import { toast } from "sonner";

const PROFILES = [
  { key: "sci", label: "SCI", icon: Building2,
    hint: "Une ou plusieurs SCI, gestion multi-associés." },
  { key: "sas", label: "SAS", icon: Landmark,
    hint: "Société par actions simplifiée — activité immobilière ou commerciale." },
  { key: "holding", label: "Holding", icon: Layers,
    hint: "Holding + filiales, consolidation, flux inter-sociétés." },
  { key: "company", label: "Entreprise", icon: Building2,
    hint: "Entreprise commerciale ou de services (hors SCI/SAS)." },
  { key: "mdb", label: "Marchand de biens", icon: Landmark,
    hint: "Opérations d'achat-revente et de rénovation." },
  { key: "private", label: "Patrimoine privé", icon: HandCoins,
    hint: "Patrimoine personnel : locatif, résidence, investissements." },
  { key: "investor", label: "Investisseur", icon: HandCoins,
    hint: "Achat locatif, rendement, gestion locative." },
  { key: "mixed", label: "Mixte", icon: Users,
    hint: "Combinaison de plusieurs profils, patrimoine varié." },
];

const SESSION_SKIP_KEY = "fonbex_onboarding_skipped_org";
const PERSIST_SKIP_KEY = "fonbex_onboarding_skipped_at";
// Durée pendant laquelle on n'insiste plus après un clic « Plus tard » : 7 jours.
const SKIP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isRecentlySkipped(orgId) {
  try {
    const raw = localStorage.getItem(PERSIST_SKIP_KEY);
    if (!raw) return false;
    const { orgId: skippedOrg, at } = JSON.parse(raw);
    if (skippedOrg !== orgId) return false;
    return (Date.now() - at) < SKIP_TTL_MS;
  } catch { return false; }
}

export default function OnboardingWizard() {
  const { user, organizations, activeOrgId, activeRole, refresh } = useAuth();
  const activeOrg = organizations.find((o) => o.id === activeOrgId);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [siren, setSiren] = useState("");
  const [city, setCity] = useState("");
  const [directorName, setDirectorName] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [activityType, setActivityType] = useState("");
  const [saving, setSaving] = useState(false);

  // Ouvre à la 1ère connexion : org active, non-démo, non complétée, user owner/admin.
  useEffect(() => {
    if (!activeOrg || !user) return;
    if (activeOrg.is_demo) return;
    if (activeOrg.onboarding_completed) return;
    if (!["owner", "admin"].includes(activeRole)) return;
    // Session skip : si l'utilisateur a cliqué « Plus tard », on ne le rouvre pas dans cette session.
    if (sessionStorage.getItem(SESSION_SKIP_KEY) === activeOrg.id) return;
    if (isRecentlySkipped(activeOrg.id)) return;
    setOrgName(activeOrg.name || "");
    setOpen(true);
    setStep(0);
  }, [activeOrg?.id, activeOrg?.onboarding_completed, activeRole, user?.id]);

  const skipForNow = () => {
    if (activeOrg?.id) {
      sessionStorage.setItem(SESSION_SKIP_KEY, activeOrg.id);
      try {
        localStorage.setItem(PERSIST_SKIP_KEY,
          JSON.stringify({ orgId: activeOrg.id, at: Date.now() }));
      } catch { /* quota / privacy mode */ }
    }
    setOpen(false);
    toast("Configuration reportée — retrouvez-la depuis Paramètres.");
  };

  const finish = async (loadDemo) => {
    setSaving(true);
    try {
      await api.patch("/organizations/current", {
        name: orgName || undefined,
        siren: siren || undefined,
        city: city || undefined,
        phone: orgPhone || undefined,
        contact_email: orgEmail || undefined,
        activity_type: activityType || undefined,
        profile_type: profile || undefined,
        onboarding_completed: true,
      });
      // director_name + billing_email persistés sur l'utilisateur/settings — non bloquants
      if (directorName) {
        try {
          const [first, ...rest] = directorName.trim().split(/\s+/);
          await api.patch("/auth/me", { first_name: first, last_name: rest.join(" ") || undefined });
        } catch { /* silencieux */ }
      }
      if (loadDemo) {
        try { await api.post("/demo/import-into-current"); }
        catch { /* best-effort, l'utilisateur peut refaire depuis Paramètres */ }
      }
      await refresh();
      setOpen(false);
      toast.success(loadDemo
        ? "Bienvenue ! Votre espace est chargé avec un jeu d'exemple."
        : "Bienvenue sur Fonbex ! Vous pouvez commencer.");
    } catch (e) {
      toast.error("Impossible de finaliser la configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* bloqué : voir « Plus tard » */ }}>
      <DialogContent hideClose className="max-w-2xl p-0 overflow-hidden"
                     data-testid="onboarding-wizard">
        <DialogTitle className="sr-only">Assistant de bienvenue Fonbex</DialogTitle>
        <DialogDescription className="sr-only">
          Renseignez votre profil et votre organisation pour personnaliser votre espace Fonbex.
        </DialogDescription>
        {/* Bandeau d'étape */}
        <div className="flex items-center justify-between px-6 pt-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Étape {step + 1} / 4
          </div>
          <button
            type="button"
            onClick={skipForNow}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="onboarding-skip">
            Continuer plus tard
          </button>
        </div>
        <div className="h-1 w-full bg-secondary/40 mt-3">
          <div className="h-full bg-accent transition-all"
               style={{ width: `${((step + 1) / 4) * 100}%` }} />
        </div>

        <div className="px-6 py-6 min-h-[360px]">
          {step === 0 && (
            <WelcomeStep user={user} />
          )}
          {step === 1 && (
            <ProfileStep profile={profile} setProfile={setProfile} />
          )}
          {step === 2 && (
            <OrgStep
              orgName={orgName} setOrgName={setOrgName}
              siren={siren} setSiren={setSiren}
              city={city} setCity={setCity}
              directorName={directorName} setDirectorName={setDirectorName}
              orgPhone={orgPhone} setOrgPhone={setOrgPhone}
              orgEmail={orgEmail} setOrgEmail={setOrgEmail}
              activityType={activityType} setActivityType={setActivityType}
            />
          )}
          {step === 3 && (
            <StartStep onFinish={finish} saving={saving} />
          )}
        </div>

        {/* Footer navigation */}
        {step < 3 && (
          <div className="flex items-center justify-between px-6 py-4 border-t bg-secondary/20">
            <Button variant="ghost" disabled={step === 0}
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    data-testid="onboarding-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Retour
            </Button>
            <Button
              onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={step === 1 && !profile}
              className="btn-pill"
              data-testid="onboarding-next">
              Continuer <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WelcomeStep({ user }) {
  return (
    <div className="text-center py-6">
      <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-accent/15 flex items-center justify-center">
        <PartyPopper className="h-7 w-7 text-accent" />
      </div>
      <h2 className="font-heading text-2xl">Bienvenue sur Fonbex, {user?.first_name} !</h2>
      <p className="mt-2 text-muted-foreground max-w-md mx-auto">
        Configurons votre espace en 4 étapes rapides. Vous pourrez ensuite gérer
        vos immeubles, sociétés, locataires et documents avec sérénité.
      </p>
      <div className="mt-6 grid grid-cols-3 gap-3 text-xs">
        {[
          ["Profil", "Investisseur, SCI, holding…"],
          ["Organisation", "Nom, coordonnées"],
          ["Démarrer", "À vide ou avec exemples"],
        ].map(([t, s]) => (
          <div key={t} className="rounded-xl bg-secondary/40 p-3">
            <div className="font-medium text-sm">{t}</div>
            <div className="text-muted-foreground mt-1">{s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileStep({ profile, setProfile }) {
  return (
    <div>
      <h2 className="font-heading text-xl">Quel est votre profil patrimonial ?</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Nous adapterons les modules et les tableaux de bord. Vous pourrez le changer plus tard.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {PROFILES.map((p) => {
          const Icon = p.icon;
          const selected = profile === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setProfile(p.key)}
              data-testid={`onboarding-profile-${p.key}`}
              className={`text-left p-4 rounded-xl border transition-all ${
                selected
                  ? "border-accent bg-accent/5 ring-2 ring-accent"
                  : "border-border hover:border-accent/50 hover:bg-secondary/30"
              }`}>
              <div className="flex items-center gap-3">
                <Icon className={`h-5 w-5 ${selected ? "text-accent" : "text-muted-foreground"}`} />
                <span className="font-medium">{p.label}</span>
                {selected && <CheckCircle2 className="h-4 w-4 text-accent ml-auto" />}
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 pl-8">{p.hint}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrgStep({ orgName, setOrgName, siren, setSiren, city, setCity,
                   directorName, setDirectorName, orgPhone, setOrgPhone,
                   orgEmail, setOrgEmail, activityType, setActivityType }) {
  return (
    <div>
      <h2 className="font-heading text-xl">Votre organisation</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Tous les champs sont optionnels. Vous pourrez tout compléter plus tard depuis Paramètres.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3 max-w-2xl">
        <div>
          <Label>Nom de l&apos;organisation</Label>
          <Input value={orgName} onChange={(e) => setOrgName(e.target.value)}
                 placeholder="Ex : SCI Étoile · SAS Patrimoine…"
                 data-testid="onboarding-org-name" className="mt-1.5" />
        </div>
        <div>
          <Label>Nom du dirigeant</Label>
          <Input value={directorName} onChange={(e) => setDirectorName(e.target.value)}
                 placeholder="Prénom Nom"
                 data-testid="onboarding-director" className="mt-1.5" />
        </div>
        <div>
          <Label>SIREN</Label>
          <Input value={siren} onChange={(e) => setSiren(e.target.value)}
                 placeholder="9 chiffres" maxLength={9}
                 data-testid="onboarding-siren" className="mt-1.5" />
        </div>
        <div>
          <Label>Ville</Label>
          <Input value={city} onChange={(e) => setCity(e.target.value)}
                 placeholder="Paris, Lyon, Bordeaux…"
                 data-testid="onboarding-city" className="mt-1.5" />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input value={orgPhone} onChange={(e) => setOrgPhone(e.target.value)}
                 placeholder="+33 1 23 45 67 89"
                 data-testid="onboarding-phone" className="mt-1.5" />
        </div>
        <div>
          <Label>E-mail de contact de l&apos;organisation</Label>
          <Input value={orgEmail} onChange={(e) => setOrgEmail(e.target.value)}
                 type="email" placeholder="contact@masocie.fr"
                 data-testid="onboarding-org-email" className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label>Type d&apos;activité (libellé libre)</Label>
          <Input value={activityType} onChange={(e) => setActivityType(e.target.value)}
                 placeholder="Ex : Gestion locative, marchand de biens, immobilier commercial…"
                 data-testid="onboarding-activity" className="mt-1.5" />
        </div>
      </div>
      <div className="text-xs text-muted-foreground bg-secondary/30 rounded-lg p-3 mt-4 max-w-2xl">
        Le logo, l&apos;adresse complète, l&apos;IBAN, le n° de TVA et le fuseau horaire
        seront disponibles à tout moment depuis <strong>Paramètres → Organisation</strong>.
      </div>
    </div>
  );
}

function StartStep({ onFinish, saving }) {
  return (
    <div>
      <div className="text-center mb-5">
        <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
          <Rocket className="h-6 w-6 text-emerald-600" />
        </div>
        <h2 className="font-heading text-xl">Comment souhaitez-vous démarrer ?</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onFinish(false)}
          disabled={saving}
          data-testid="onboarding-start-empty"
          className="text-left p-5 rounded-xl border border-border hover:border-accent/60
                     hover:bg-secondary/30 transition-all disabled:opacity-50">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <div className="font-semibold">Partir de zéro</div>
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            Espace vide : vous ajoutez vos vraies sociétés, immeubles et locataires
            au fur et à mesure.
          </div>
        </button>
        <button
          type="button"
          onClick={() => onFinish(true)}
          disabled={saving}
          data-testid="onboarding-start-demo"
          className="text-left p-5 rounded-xl border-2 border-accent bg-accent/5
                     hover:bg-accent/10 transition-all disabled:opacity-50 relative">
          <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest
                          bg-accent text-accent-foreground rounded-full px-2 py-0.5">
            Recommandé
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" />
            <div className="font-semibold">Charger un jeu d&apos;exemple</div>
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            Découvrez Fonbex avec des SCI, immeubles et locataires fictifs
            dans votre propre organisation. Vous pourrez tout supprimer plus tard.
          </div>
        </button>
      </div>
      <div className="text-xs text-center text-muted-foreground mt-4">
        {saving ? "Configuration en cours…" : "Vous pouvez modifier tout cela à tout moment."}
      </div>
    </div>
  );
}
